import { Heading, Subheading } from "../../../../design/heading";
import { Divider } from "../../../../design/divider";
import { Text } from "../../../../design/text";
import { Avatar } from "../../../../design/avatar";
import EditProfileButton from "./components/EditProfileButton";
import RefreshRolesButton from "./components/RefreshRolesButton";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../../../design/description-list";
import { useUser } from "../../../../contexts/UserContext";
import { useAuth } from "../../../../contexts/AuthContext";
import SettingsHeader from "../SettingsHeader";
import { getInitials } from "../../../../utils/string";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Badge } from "../../../../design/badge";

export default function UserInformation() {
  const { currentUser } = useUser();
  const { role } = useAuth();

  const fullName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : '';
  const initials = getInitials(fullName);

  const getRoleLabel = (role: string | null) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'supervisor': return 'Monitor';
      case 'parent': return 'Parent';
      default: return 'User';
    }
  };

  const getRoleColor = (role: string | null): "blue" | "green" | "purple" | "zinc" => {
    switch (role) {
      case 'admin': return 'blue';
      case 'supervisor': return 'green';
      case 'parent': return 'purple';
      default: return 'zinc';
    }
  };

  return (
    <>
      <SettingsHeader />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <Heading>Profile</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Manage your personal information and account settings.
          </Text>
        </div>

        {/* Profile Card */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                src={currentUser?.avatarUrl}
                initials={initials}
                className="size-16"
              />
              <div>
                <Subheading>{fullName || 'No name set'}</Subheading>
                <div className="flex items-center gap-2 mt-1">
                  <Badge color={getRoleColor(role)}>
                    <span className="flex items-center gap-1">
                      <ShieldCheckIcon className="w-3 h-3" />
                      {getRoleLabel(role)}
                    </span>
                  </Badge>
                </div>
              </div>
            </div>
            <EditProfileButton />
          </div>
        </div>

        {/* Contact Information */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <Subheading>Contact Information</Subheading>
          </div>

          <DescriptionList>
            <DescriptionTerm>
              <span className="flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-zinc-400" />
                Full Name
              </span>
            </DescriptionTerm>
            <DescriptionDetails>
              {fullName || <span className="text-zinc-400 dark:text-zinc-500">Not set</span>}
            </DescriptionDetails>

            <DescriptionTerm>
              <span className="flex items-center gap-2">
                <EnvelopeIcon className="w-4 h-4 text-zinc-400" />
                Email Address
              </span>
            </DescriptionTerm>
            <DescriptionDetails>
              {currentUser?.email || <span className="text-zinc-400 dark:text-zinc-500">Not set</span>}
            </DescriptionDetails>

            <DescriptionTerm>
              <span className="flex items-center gap-2">
                <PhoneIcon className="w-4 h-4 text-zinc-400" />
                Phone Number
              </span>
            </DescriptionTerm>
            <DescriptionDetails>
              {currentUser?.phoneNumber || <span className="text-zinc-400 dark:text-zinc-500">Not set</span>}
            </DescriptionDetails>
          </DescriptionList>
        </div>

        <Divider />

        {/* Account Settings */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading className="mb-4">Account Settings</Subheading>
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <Text className="font-medium">Refresh Roles & Permissions</Text>
                <Text className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                  Sync your roles and permissions from the server
                </Text>
              </div>
              <RefreshRolesButton />
            </div>
          </div>
        </div>

        <Divider />

        {/* Info Section */}
        <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
          <Subheading className="mb-2">About Your Profile</Subheading>
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            Your profile information is used to identify you across VisitProof.
            Keep your contact details up to date to ensure you receive important notifications.
          </Text>
        </div>
      </div>
    </>
  );
}
